/**
 * F-147: a board created from an imported file is named after that file —
 * the creation day as `yyyyMMMdd_` (owner's format, 2026-10-08: `2026Oct08_`)
 * followed by a few English keywords for what the video is about.
 *
 * The keywords come from the server (`POST /api/boards/name-suggestion`,
 * one small AI call — the file is often named in Korean or Chinese, and
 * "short English keywords" is a translation, not a string operation). This
 * module holds everything around that call that both create dialogs share:
 * the date prefix, the clean-up every keyword string goes through, and the
 * offline fallback built from the file name when the call fails.
 */
/** How much of the file rides along with its name. */
export const BOARD_NAME_EXCERPT_CHARS = 2000;
/** The keyword part's ceiling, in characters (the date prefix is extra). */
export const BOARD_NAME_MAX_KEYWORD_CHARS = 40;
/** The keyword part's ceiling, in words. */
export const BOARD_NAME_MAX_WORDS = 4;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** `2026-10-08` → `2026Oct08_`. English month names whatever the locale. */
export function boardNameDatePrefix(dateKey) {
    const [year, month, day] = dateKey.split("-");
    return `${year}${MONTHS[Number(month) - 1] ?? month}${day}_`;
}
/** The part of a file's text the server sees. */
export function boardNameExcerpt(text) {
    return text.slice(0, BOARD_NAME_EXCERPT_CHARS);
}
/**
 * The one shape every keyword string ends in, whoever wrote it: ASCII words
 * separated by single spaces, at most `BOARD_NAME_MAX_WORDS` of them and
 * `BOARD_NAME_MAX_KEYWORD_CHARS` characters, an all-lowercase word
 * capitalised ("ramen" → "Ramen") and any other casing kept ("AI",
 * "iPhone"). Anything else — punctuation, emoji, a script the model was told
 * to translate and didn't — is a separator. "" when nothing is left.
 */
export function normalizeBoardKeywords(raw) {
    const words = raw
        .replace(/['’]/g, "")
        .split(/[^A-Za-z0-9]+/)
        .filter((word) => word.length > 0)
        .slice(0, BOARD_NAME_MAX_WORDS)
        .map((word) => (word === word.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word));
    let out = "";
    for (const word of words) {
        const next = out === "" ? word : `${out} ${word}`;
        if (next.length > BOARD_NAME_MAX_KEYWORD_CHARS)
            break;
        out = next;
    }
    return out;
}
/**
 * Words a file name carries about the FILE rather than the video: every
 * import is some draft of a script or storyboard, so naming the board after
 * that would name every board the same.
 */
const FILE_NOISE = /^(v\d+|ver|version|rev\d*|revised|final|draft|copy|script|scripts|storyboard|storyboards|untitled|new|latest|wip|edit|edited|kola|md|csv|tsv|txt)$/i;
/** A file name minus its extension (a 1–5 character suffix after the last dot). */
export function fileStem(fileName) {
    return fileName.replace(/\.[A-Za-z0-9]{1,5}$/, "");
}
/**
 * The offline fallback: English keywords taken from the file name alone —
 * camelCase split, noise words and long digit runs (dates, versions)
 * dropped. "" for a name with no English left in it (`대본_최종.md`); the
 * caller then falls back to the stem as it is.
 */
export function fileNameKeywords(fileName) {
    const words = fileStem(fileName)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(/[^A-Za-z0-9]+/)
        .filter((word) => word.length > 0 && !FILE_NOISE.test(word) && !/^\d{3,}$/.test(word));
    return normalizeBoardKeywords(words.join(" "));
}
/**
 * The name a board created from `fileName` gets on `dateKey` (the creation
 * day, `YYYY-MM-DD`): the server's keywords when it had any, else the file
 * name's own English words, else the file name as it is.
 */
export function boardNameFromFile(dateKey, fileName, keywords) {
    const part = normalizeBoardKeywords(keywords ?? "") ||
        fileNameKeywords(fileName) ||
        fileStem(fileName).replace(/[_\s]+/g, " ").trim().slice(0, BOARD_NAME_MAX_KEYWORD_CHARS).trim() ||
        "Imported";
    return `${boardNameDatePrefix(dateKey)}${part}`;
}
//# sourceMappingURL=board-name.js.map